import { Column, TaskView } from "src/api/types";
import { format } from "date-fns";
import { AlertColor, IconButton, Typography } from "@mui/material";
import { CancelOutlined } from "@mui/icons-material";
import { useUpdateTaskMutation } from "src/api/services/tasksApi";
import { useCallback, useMemo } from "react";
import { isNonProviderView } from "src/utils/isNonProviderView";
import { useLocation } from "react-router-dom";

export const useColumns = (
  setAlert?: (alert: { text: string; severity: AlertColor }) => void,
): Column<TaskView>[] => {
  const [updateTask] = useUpdateTaskMutation();
  const { pathname } = useLocation();

  const isNPV = useMemo(() => {
    return isNonProviderView(pathname);
  }, [pathname]);

  const handleCancelTask = useCallback(
    async (id: number) => {
      await updateTask({
        id,
        body: {
          action_taken: "CANCELED",
          completed_at: new Date().toISOString(),
        },
      })
        .unwrap()
        .then(() =>
          setAlert != null
            ? setAlert({
                text: `Task ${id} has been canceled`,
                severity: "warning",
              })
            : null,
        );
    },
    [setAlert, updateTask],
  );

  const columns: Column<TaskView>[] = [
    {
      id: "cancel",
      label: "Cancel",
      minWidth: 80,
      align: "center",
      orderable: false,
      format: (value) => value.id.toString(),
      render: (value) => (
        <IconButton
          size="small"
          disabled={value.action_taken !== "INCOMPLETE"}
          onClick={() => handleCancelTask(value.id)}
          color="error"
        >
          <CancelOutlined />
        </IconButton>
      ),
    },
    {
      id: "id",
      label: "Task ID",
      minWidth: 100,
      align: "center",
      orderable: false,
      format: (value) => value.id.toString(),
      render: (value) => <Typography>{value.id}</Typography>,
    },
    {
      id: "task_type",
      label: "Task Type",
      minWidth: 200,
      align: "center",
      orderable: false,
      format: (value) => value.task_type ?? "N/A",
      render: (value) => <Typography>{value.task_type ?? "N/A"}</Typography>,
    },
    {
      id: "created_at",
      label: "Task Date",
      minWidth: 150,
      align: "center",
      orderable: false,
      format: (value) => format(new Date(value.created_at || ""), "MM/dd/yyyy"),
      render: (value) => {
        const date = format(new Date(value.created_at || ""), "MM/dd/yyyy");

        return <Typography>{date}</Typography>;
      },
    },
    {
      id: "note",
      label: "Note",
      minWidth: 150,
      align: "center",
      orderable: false,
      format: (value) => value.note ?? "N/A",
      render: (value) => <Typography>{value.note ?? "N/A"}</Typography>,
    },
    {
      id: "action_taken",
      label: "Action Taken",
      minWidth: 150,
      align: "center",
      orderable: false,
      format: (value) => value.action_taken ?? "N/A",
      render: (value) => <Typography>{value.action_taken ?? "N/A"}</Typography>,
    },
    {
      id: "assignee",
      label: "Assigned",
      minWidth: 150,
      align: "center",
      orderable: false,
      format: (value) =>
        `${value.assignee_first_name ?? ""} ${value.assignee_last_name ?? ""}`,
      render: (value) => (
        <Typography>{`${value.assignee_first_name ?? ""} ${value.assignee_last_name ?? ""}`}</Typography>
      ),
    },
    {
      id: "queue",
      label: "Queue Type",
      minWidth: 150,
      align: "center",
      orderable: false,
      format: (value) => value.queue ?? "N/A",
      render: (value) => {
        if (value.queue === "Coordinator") {
          return <Typography>{"Health Navigator"}</Typography>;
        }

        return <Typography>{value.queue ?? "N/A"}</Typography>;
      },
    },
  ];

  return isNPV ? columns : columns.filter((c) => c.id !== "cancel");
};
